import { S3Client, PutObjectCommand, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

/**
 * Configuration R2 Cloudflare depuis .env
 */
const R2_CONFIG = {
  endpoint: process.env.REACT_APP_R2_ENDPOINT,
  region: "auto",
  credentials: {
    accessKeyId: process.env.REACT_APP_R2_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_R2_SECRET_ACCESS_KEY
  }
};

const BUCKET_NAME = process.env.REACT_APP_R2_BUCKET_NAME || "feuilles-temps";

// Client S3 pour R2
const r2Client = new S3Client(R2_CONFIG);

/**
 * Uploader un PDF sur R2
 * @param {Blob} pdfBlob - Le blob PDF à uploader
 * @param {string} employeeEmail - Email de l'employé
 * @param {string} monthStart - Date de début du mois (YYYY-MM-DD)
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export async function uploadPdfToR2(pdfBlob, employeeEmail, monthStart) {
  try {
    console.log('📤 Upload PDF vers R2...');
    
    // Créer un nom de fichier unique et sécurisé
    const timestamp = new Date().getTime();
    const safeEmail = employeeEmail.replace(/[^a-zA-Z0-9]/g, '_');
    const fileName = `${safeEmail}/${monthStart}_${timestamp}.pdf`;
    
    console.log('📁 Nom du fichier:', fileName);
    
    // Convertir le Blob en ArrayBuffer
    const arrayBuffer = await pdfBlob.arrayBuffer();
    const buffer = new Uint8Array(arrayBuffer);
    
    // Préparer la commande d'upload
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: fileName,
      Body: buffer,
      ContentType: 'application/pdf',
      Metadata: {
        employeeEmail: employeeEmail,
        monthStart: monthStart,
        uploadedAt: new Date().toISOString()
      }
    });
    
    // Uploader sur R2
    await r2Client.send(command);
    
    console.log('✅ PDF uploadé avec succès sur R2');
    
    // Construire l'URL du fichier
    // Note: Cette URL nécessite un domaine public configuré sur R2
    // Ou utiliser une URL signée (voir generateSignedUrl ci-dessous)
    const fileUrl = `${R2_CONFIG.endpoint}/${BUCKET_NAME}/${fileName}`;
    
    return {
      success: true,
      url: fileUrl,
      fileName: fileName
    };
    
  } catch (error) {
    console.error('❌ Erreur upload R2:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Générer une URL signée pour télécharger un PDF
 * (URL temporaire valide 24h)
 * @param {string} fileName - Nom du fichier dans R2
 * @returns {Promise<string>}
 */
export async function generateSignedUrl(fileName) {
  try {
    const command = new GetObjectCommand({
      Bucket: BUCKET_NAME,
      Key: fileName
    });
    
    // URL signée valide pendant 24 heures
    const signedUrl = await getSignedUrl(r2Client, command, { expiresIn: 86400 });
    
    return signedUrl;
    
  } catch (error) {
    console.error('❌ Erreur génération URL signée:', error);
    throw error;
  }
}

/**
 * Vérifier si le bucket est accessible
 * @returns {Promise<boolean>}
 */
export async function testR2Connection() {
  try {
    // Tenter de lister les objets (juste pour tester la connexion)
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: 'test-connection.txt',
      Body: 'Connection test',
      ContentType: 'text/plain'
    });
    
    await r2Client.send(command);
    console.log('✅ Connexion R2 OK');
    return true;
    
  } catch (error) {
    console.error('❌ Erreur connexion R2:', error);
    return false;
  }
}

export default {
  uploadPdfToR2,
  generateSignedUrl,
  testR2Connection
};